import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  const solid = !isHome || scrolled || mobileOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur border-b border-background-200/70" : "bg-transparent"
      }`}
    >
      <nav className="container-x flex h-16 items-center justify-between gap-4 md:h-20" aria-label="主导航">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="漫画网首页">
          <span className={`flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50`}>
            <i className="ri-book-open-fill text-lg"></i>
          </span>
          <span className={`font-display text-xl font-bold tracking-tight ${solid ? "text-foreground-900" : "text-white"}`}>
            漫画网
          </span>
        </Link>

        {/* Desktop menu */}
        <div className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700" : "text-white/90 hover:bg-white/15"
            } ${isHome ? "font-bold text-primary-500" : ""}`}
          >
            首页
          </Link>

          {/* Category dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              onMouseEnter={() => setCatOpen(true)}
              aria-expanded={catOpen}
              className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                solid ? "text-foreground-700 hover:bg-background-100" : "text-white/90 hover:bg-white/15"
              }`}
            >
              漫画分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div
                className="absolute left-1/2 top-full w-56 -translate-x-1/2 rounded-lg border border-background-200/70 bg-background-50 p-2 shadow-lg"
                onMouseLeave={() => setCatOpen(false)}
              >
                <Link
                  to="/category/all"
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                >
                  <i className="ri-apps-2-fill text-base text-primary-500"></i>
                  全部漫画
                </Link>
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                  >
                    <i className={`${c.icon} text-base text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/category/all?sort=views"
            className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700" : "text-white/90 hover:bg-white/15"
            }`}
          >
            排行榜
          </Link>
          <Link
            to="/about"
            className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700" : "text-white/90 hover:bg-white/15"
            }`}
          >
            关于我们
          </Link>
        </div>

        <div className="hidden md:block">
          <Link
            to="/category/all"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line text-base"></i>
            开始看漫画
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
            solid ? "text-foreground-800" : "text-white"
          }`}
          aria-label="打开菜单"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-background-200/70 bg-background-50 md:hidden">
          <div className="container-x flex flex-col gap-1 py-4">
            <Link to="/" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <Link to="/category/all" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              全部漫画
            </Link>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className={`${c.icon} text-base text-primary-500`}></i>
                  {c.name}
                </Link>
              ))}
            </div>
            <Link to="/about" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              关于我们
            </Link>
            <Link
              to="/category/all"
              className="mt-2 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50"
            >
              <i className="ri-search-line text-base"></i>
              开始看漫画
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}